import { FetchError } from 'ofetch';
import * as Sentry from '@sentry/vue';
import { resolveCodedMessage } from '~/utils/i18n/errorCode';

const useErrorHandler = () => {
  // `catchErrors` is frequently invoked from async callbacks (mutation `onError`,
  // a `.catch()` handler, an event handler fired well after this composable's own
  // setup-time call) where `useI18n()` can't resolve a component instance. `$i18n.t`
  // via `useNuxtApp()` works in both contexts, so every message is looked up through
  // it — never memoized into a top-level const — to stay correct if the locale
  // changes between calls.
  const { $i18n } = useNuxtApp();
  const { showSnackBanner } = useUIStore();
  const { logout } = useSuccessfulLogout();
  // Resolved at setup: catchErrors often runs after an await, where useState-based composables throw.
  const uaeGate = useUaeGate();

  const defaultErrorMessage = () => $i18n.t('errors.default');

  const captureException = (error: any) => {
    if (process.client) {
      Sentry.captureException(error);
    }
  };

  const isFetchError = (error: any) => {
    return error instanceof FetchError;
  };

  // `errorMessage` is the raw (English) server text — the suppression checks below
  // compare against it; `displayMessage` is what the user sees (translated when the
  // server attached a known error code).
  const handleErrorResponse = (errorMessage: string, displayMessage: string, error: any, status: number | null) => {
    switch (status) {
      case 401:
        if (errorMessage !== 'Token is invalid or expired' && errorMessage !== 'User not found') {
          showSnackBanner('error', displayMessage);
        }
        logout();
        break;
      case 403:
        showSnackBanner('error', displayMessage);
        break;
      case 500:
        captureException(error);
        showSnackBanner('error', defaultErrorMessage());
        break;
      case 404:
        if (errorMessage !== 'User not found') {
          captureException(error);
          showSnackBanner('error', displayMessage);
        }
        break;
      default:
        captureException(error);
        showSnackBanner('error', displayMessage);
    }
  };

  // Server routes attach `data: { code }` to user-facing errors (utils/i18n/errorCode.ts).
  // Only a code with a catalog entry is used; anything else keeps the server text.
  const getCodedMessage = (error: unknown): string | null =>
    resolveCodedMessage(error, { t: (key, params) => $i18n.t(key, params ?? {}), te: (key) => $i18n.te(key) });

  const getErrorMessage = (error: any) => {
    if (isFetchError(error) && error.response?._data.message) {
      return error.response._data.message;
    }

    if (isNuxtError(error)) {
      return error.message;
    }

    return defaultErrorMessage();
  };

  const getErrorStatus = (error: any) => {
    if (isFetchError(error) && error.response?.status) {
      return error.response.status;
    }

    if (isNuxtError(error)) {
      return error.statusCode;
    }

    return null;
  };

  const catchErrors = (error: any) => {
    // eslint-disable-next-line security-node/detect-crlf
    console.log(error);

    // UAE level / limit / no-subscription responses open the next-step sheet instead of a toast.
    if (uaeGate.openFromError(error)) return;

    // Read-only impersonation: the global write-block middleware rejects any
    // mutating request while "viewing as" an agent. Surface a friendly notice
    // (via the app's standard snack banner) instead of the generic error path.
    const status = error?.statusCode ?? error?.response?.status;
    const statusMessage = error?.data?.statusMessage ?? error?.response?._data?.statusMessage ?? error?.statusMessage;
    if (status === 403 && statusMessage === 'read-only-impersonation') {
      showSnackBanner('info', $i18n.t('errors.impersonation.readOnly'));
      return;
    }
    // "View as" rejected by the start endpoint — show a friendly reason instead
    // of the generic error (e.g. clicking View-as on a non-agent or out-of-scope row).
    if (status === 403 && ['target-not-agent', 'out-of-am-scope', 'not-authorized', 'nested'].includes(statusMessage)) {
      const msg =
        statusMessage === 'out-of-am-scope'
          ? $i18n.t('errors.impersonation.outOfAmScope')
          : statusMessage === 'target-not-agent'
            ? $i18n.t('errors.impersonation.targetNotAgent')
            : $i18n.t('errors.impersonation.notAuthorized');
      showSnackBanner('error', msg);
      return;
    }

    if (isFetchError(error) || isNuxtError(error)) {
      const errorMessage = getErrorMessage(error);
      handleErrorResponse(errorMessage, getCodedMessage(error) ?? errorMessage, error, getErrorStatus(error));
    } else {
      captureException(error);
      showSnackBanner('error', defaultErrorMessage());
    }
  };

  return {
    catchErrors,
  };
};

export default useErrorHandler;
